import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import { canWriteGuestPitchBooking, canWriteStammdaten } from "@/lib/permissions";
import { usePageHeader } from "@/layout/usePageHeader";
import { SitePlanCanvas } from "@/components/site-plan/SitePlanCanvas";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { StammdatenWriteHint } from "@/pages/stammdaten/StammdatenWriteHint";
import {
  DEFAULT_BADGE_SCALE,
  MAX_BADGE_SCALE,
  MIN_BADGE_SCALE,
  readImageFileAsDataUrl,
  type MapPoint,
  type SitePlanSettings,
} from "@/lib/sitePlan";
import type { Pitch } from "@/types/camping";

const MAX_IMAGE_BYTES = 4 * 1024 * 1024;

export function SitePlanStammdatenPage() {
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const canWritePlan = canWriteStammdaten(groups);
  const canDraw = canWriteGuestPitchBooking(groups);

  const [pitches, setPitches] = useState<Pitch[]>([]);
  const [settings, setSettings] = useState<SitePlanSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [selectedPitchId, setSelectedPitchId] = useState<string>("");
  const [draftPoints, setDraftPoints] = useState<MapPoint[]>([]);
  const [badgeScale, setBadgeScale] = useState(DEFAULT_BADGE_SCALE);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.get<Pitch[]>("/api/v1/pitches"),
      api.get<SitePlanSettings>("/api/v1/settings/site-plan"),
    ])
      .then(([pitchList, planSettings]) => {
        setPitches(pitchList.filter((p) => p.is_active));
        setSettings(planSettings);
        setBadgeScale(Number.parseFloat(planSettings.badge_scale) || DEFAULT_BADGE_SCALE);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setPageMeta({ title: "Stammdaten", subtitle: "Platzplan" });
    load();
    return () => setPageMeta(null);
  }, [load, setPageMeta]);

  const selectedPitch = pitches.find((p) => p.id === selectedPitchId) ?? null;

  useEffect(() => {
    if (!selectedPitch) {
      setDraftPoints([]);
      return;
    }
    setDraftPoints(selectedPitch.map_polygon ?? []);
  }, [selectedPitchId, selectedPitch?.map_polygon]);

  const overlays = useMemo(
    () =>
      pitches.map((p) => ({
        id: p.id,
        code: p.code,
        map_polygon: p.map_polygon ?? null,
        status_kind: "free" as const,
      })),
    [pitches],
  );

  async function onImageSelected(file: File | null) {
    if (!file || !canWritePlan) return;
    setError(null);
    setSuccess(null);
    if (!file.type.startsWith("image/")) {
      setError("Bitte eine Bilddatei wählen (PNG, JPEG, WebP).");
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      setError("Das Bild ist zu groß (max. 4 MB).");
      return;
    }
    try {
      const imageData = await readImageFileAsDataUrl(file);
      setSaving(true);
      const updated = await api.patch<SitePlanSettings>("/api/v1/settings/site-plan", {
        image_data: imageData,
      });
      setSettings(updated);
      setSuccess("Platzplan-Bild gespeichert.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  async function saveBadgeScale() {
    if (!canWritePlan) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const updated = await api.patch<SitePlanSettings>("/api/v1/settings/site-plan", {
        badge_scale: String(badgeScale),
      });
      setSettings(updated);
      setBadgeScale(Number.parseFloat(updated.badge_scale) || DEFAULT_BADGE_SCALE);
      setSuccess("Beschriftungsgröße gespeichert.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  async function removeImage() {
    if (!canWritePlan) return;
    setSaving(true);
    setError(null);
    try {
      const updated = await api.patch<SitePlanSettings>("/api/v1/settings/site-plan", {
        clear_image: true,
      });
      setSettings(updated);
      setSuccess("Platzplan-Bild entfernt.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Entfernen fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  async function savePolygon() {
    if (!canDraw || !selectedPitchId || draftPoints.length < 3) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await api.patch<Pitch>(`/api/v1/pitches/${selectedPitchId}`, {
        map_polygon: draftPoints,
      });
      setSuccess(`Fläche für ${selectedPitch?.code ?? "Platz"} gespeichert.`);
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  const handleMapClick = useCallback(
    (pt: MapPoint) => {
      if (!canDraw || !selectedPitchId) return;
      setDraftPoints((pts) => [...pts, pt]);
    },
    [canDraw, selectedPitchId],
  );

  const handleDraftPointMove = useCallback(
    (index: number, pt: MapPoint) => {
      if (!canDraw || !selectedPitchId) return;
      setDraftPoints((pts) => pts.map((p, i) => (i === index ? pt : p)));
    },
    [canDraw, selectedPitchId],
  );

  async function clearPolygon() {
    if (!canDraw || !selectedPitchId) return;
    setSaving(true);
    setError(null);
    try {
      await api.patch<Pitch>(`/api/v1/pitches/${selectedPitchId}`, { map_polygon: [] });
      setDraftPoints([]);
      setSuccess("Fläche entfernt.");
      load();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Löschen fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="space-y-6">
      {!canWritePlan && !canDraw ? <StammdatenWriteHint /> : null}

      <div className="rounded-2xl border border-separator bg-accent-soft/40 px-4 py-3 text-sm text-label">
        <span className="font-medium">Neu:</span> Im{" "}
        <Link to="/stammdaten/platzplan-editor" className="font-medium text-accent underline underline-offset-2">
          Platzplan-Editor
        </Link>{" "}
        kann ein Planbild per KI in eine Vektorgrafik (SVG) umgewandelt werden. Dort lassen sich Flächen bearbeiten,
        Stellplätze zuordnen und Symbole wie Bäume oder Stromkästen platzieren. Das Ergebnis kann hier als Platzplan-Bild
        veröffentlicht werden.
      </div>

      <Card title="Platzplan-Bild" bodyClassName="space-y-4">
        {loading ? (
          <p className="text-sm text-label-secondary">Laden…</p>
        ) : (
          <>
            {canWritePlan ? (
              <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                <label className="eh-btn eh-btn--secondary inline-flex min-h-[44px] cursor-pointer items-center justify-center px-4 text-sm font-medium">
                  Bild hochladen
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="sr-only"
                    disabled={saving}
                    onChange={(e) => void onImageSelected(e.target.files?.[0] ?? null)}
                  />
                </label>
                {settings?.image_data ? (
                  <Button
                    type="button"
                    variant="secondary"
                    disabled={saving}
                    onClick={() => void removeImage()}
                  >
                    Bild entfernen
                  </Button>
                ) : null}
              </div>
            ) : null}

            <label className="flex max-w-md flex-col gap-2 text-sm">
              <span className="font-medium text-label">
                Beschriftungsgröße ({Math.round(badgeScale * 100)} %)
              </span>
              <input
                type="range"
                min={MIN_BADGE_SCALE}
                max={MAX_BADGE_SCALE}
                step="0.05"
                value={badgeScale}
                disabled={!canWritePlan}
                onChange={(e) => setBadgeScale(Number.parseFloat(e.target.value))}
                className="min-h-[44px] w-full"
              />
              {canWritePlan ? (
                <Button
                  type="button"
                  className="self-start"
                  loading={saving}
                  onClick={() => void saveBadgeScale()}
                >
                  Beschriftungsgröße speichern
                </Button>
              ) : null}
            </label>
          </>
        )}
      </Card>

      <Card title="Stellplätze nachzeichnen" bodyClassName="space-y-4">
        {!canDraw ? (
          <p className="text-sm text-label-secondary">
            Zum Nachzeichnen der Plätze sind Schreibrechte für Plätze erforderlich.
          </p>
        ) : (
          <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
            <label className="flex min-w-[200px] flex-1 flex-col gap-1 text-sm">
              Stellplatz
              <select
                className="eh-input min-h-[44px]"
                value={selectedPitchId}
                onChange={(e) => setSelectedPitchId(e.target.value)}
              >
                <option value="">Bitte wählen…</option>
                {pitches.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.code}
                    {p.map_polygon?.length ? " ✓" : ""}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                variant="secondary"
                disabled={!selectedPitchId || draftPoints.length === 0}
                onClick={() => setDraftPoints((pts) => pts.slice(0, -1))}
              >
                Letzten Punkt entfernen
              </Button>
              <Button
                type="button"
                variant="secondary"
                disabled={!selectedPitchId}
                onClick={() => void clearPolygon()}
              >
                Fläche löschen
              </Button>
              <Button
                type="button"
                disabled={!selectedPitchId || draftPoints.length < 3}
                loading={saving}
                onClick={() => void savePolygon()}
              >
                Fläche speichern ({draftPoints.length} Punkte)
              </Button>
            </div>
          </div>
        )}

        {error ? <p className="text-sm text-status-red">{error}</p> : null}
        {success ? <p className="text-sm text-status-green">{success}</p> : null}

        <SitePlanCanvas
          imageData={settings?.image_data ?? null}
          pitches={overlays}
          badgeScale={badgeScale}
          mode={canDraw && selectedPitchId ? "edit" : "view"}
          selectedPitchId={selectedPitchId || null}
          draftPoints={draftPoints}
          onMapClick={handleMapClick}
          onDraftPointMove={handleDraftPointMove}
          onPitchSelect={setSelectedPitchId}
        />

        <p className="text-xs text-label-tertiary">
          Setzen Sie mindestens drei Eckpunkte entlang der Platzgrenze. Ziehen Sie nummerierte
          Marker, um einen Punkt zu verschieben. Die Platzkennung erscheint automatisch in der Mitte
          der Fläche.
        </p>
      </Card>
    </div>
  );
}
